import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { ApiError, onSessionExpired, SessionExpiredError, tokens } from './api';
import { platform, type Operator, type PlatformPermission, type SignInInput } from './platform';

/**
 * Who the operator is, according to the server.
 *
 * `GET /me` is the only source. Nothing is decoded from the token and nothing
 * is cached: role and permissions are read from the row on every call, so a
 * demotion takes effect on the next navigation, not at token expiry.
 *
 * `has()` decides what the panel *offers*. It never decides what is *allowed*
 * — the API re-checks every permission on every request. It is here so the
 * interface does not dangle a button that answers 403.
 *
 * The panel is platform-only. The tenant session that used to sit beside this
 * (`lib/session.tsx`) is gone with the tenant screens it served.
 */
export type OperatorStatus =
  /** Checking a stored session on boot. */
  | 'checking'
  | 'signed-out'
  | 'signed-in'
  /** A stored session exists but `/me` failed for a reason other than auth — offline, 5xx. Not a sign-out. */
  | 'unreachable';

export interface OperatorValue {
  status: OperatorStatus;
  operator: Operator | null;
  /** The failure behind `unreachable`. */
  error: unknown;
  /** Every permission held. */
  has: (...permissions: PlatformPermission[]) => boolean;
  /**
   * Throws the API's refusal as-is (`ApiError`): 401 wrong credentials, 429
   * rate-limited (`retryAfter`), field errors on 400. The page shows them.
   */
  signIn: (credentials: SignInInput) => Promise<Operator>;
  signOut: () => Promise<void>;
  /** Re-read `/me` — after an `unreachable` boot, or after a change to the operator's own rank. */
  reload: () => Promise<void>;
}

const OperatorContext = createContext<OperatorValue | null>(null);

export function OperatorProvider({ children }: { children: ReactNode }) {
  // No stored session means nothing to check: render sign-in immediately
  // rather than spending a round trip on a 401 that is already known.
  const [status, setStatus] = useState<OperatorStatus>(() => (tokens.present() ? 'checking' : 'signed-out'));
  const [operator, setOperator] = useState<Operator | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!tokens.present()) {
      setOperator(null);
      setStatus('signed-out');

      return;
    }

    try {
      const me = await platform.auth.me(signal === undefined ? {} : { signal });

      setOperator(me);
      setError(null);
      setStatus('signed-in');
    } catch (failure) {
      if (signal?.aborted === true) return;

      if (failure instanceof SessionExpiredError || (failure instanceof ApiError && failure.isUnauthorized)) {
        tokens.clear();
        setOperator(null);
        setStatus('signed-out');

        return;
      }

      // 5xx, offline, a 403 that should not happen on `/me`: the session may be
      // fine. Sending the operator to sign in would be a lie about the cause.
      setError(failure);
      setStatus('unreachable');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (tokens.present()) void load(controller.signal);

    return () => controller.abort();
  }, [load]);

  // A refresh refused anywhere in the panel ends the session everywhere. The
  // route guard sees `signed-out` and redirects with `?next=` set.
  useEffect(
    () =>
      onSessionExpired(() => {
        setOperator(null);
        setStatus('signed-out');
      }),
    [],
  );

  const signIn = useCallback(async (credentials: SignInInput): Promise<Operator> => {
    tokens.write(await platform.auth.signIn(credentials));

    try {
      const me = await platform.auth.me();

      setOperator(me);
      setError(null);
      setStatus('signed-in');

      return me;
    } catch (failure) {
      // Tokens without an operator to go with them are a half-signed-in tab.
      tokens.clear();

      throw failure;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await platform.auth.signOut();
    } catch {
      // Unreachable or already revoked: either way the tokens go below.
    } finally {
      // Discarding the tokens is not what ends the session — the server-side
      // watermark is — but leaving them behind on a network error is how
      // somebody stays signed in on a shared machine.
      tokens.clear();
      setOperator(null);
      setStatus('signed-out');
    }
  }, []);

  const value = useMemo<OperatorValue>(
    () => ({
      status,
      operator,
      error,
      has: (...permissions) => operator !== null && permissions.every((permission) => operator.permissions.includes(permission)),
      signIn,
      signOut,
      reload: () => load(),
    }),
    [status, operator, error, signIn, signOut, load],
  );

  return <OperatorContext.Provider value={value}>{children}</OperatorContext.Provider>;
}

export function useOperator(): OperatorValue {
  const value = useContext(OperatorContext);

  if (value === null) throw new Error('useOperator must be used inside <OperatorProvider>');

  return value;
}
